import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, Eye, MessageSquare, BarChart3, RefreshCw, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AnalyticsPanel Component
 * Campaign analytics dashboard with metrics and charts
 */
export default function AnalyticsPanel() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const [analytics, setAnalytics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/5">
        <CardContent className="p-6 flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Campaign Analytics</h3>
          <p className="text-sm text-muted-foreground">
            Performance overview for the last {period.replace('d', ' days')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { 
            label: "Total Impressions", 
            value: analytics?.total_impressions?.toLocaleString() || "0",
            icon: Eye,
            color: "text-blue-500",
            bgColor: "bg-blue-500/10"
          },
          { 
            label: "Total Engagements", 
            value: analytics?.total_engagements?.toLocaleString() || "0",
            icon: MessageSquare,
            color: "text-green-500",
            bgColor: "bg-green-500/10"
          },
          { 
            label: "Engagement Rate", 
            value: `${analytics?.engagement_rate || 0}%`,
            icon: TrendingUp,
            color: "text-purple-500",
            bgColor: "bg-purple-500/10"
          },
          { 
            label: "Growth Rate", 
            value: `+${analytics?.growth_rate || 0}%`,
            icon: BarChart3,
            color: "text-orange-500",
            bgColor: "bg-orange-500/10"
          }
        ].map((metric, i) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="glass-card border-white/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <metric.icon className={`w-4 h-4 ${metric.color}`} />
                  </div>
                  <div>
                    <p className="text-xl font-bold">{metric.value}</p>
                    <p className="text-xs text-muted-foreground">{metric.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Platform Breakdown */}
      {analytics?.platform_distribution && Object.keys(analytics.platform_distribution).length > 0 && (
        <Card className="glass-card border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Platform Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(analytics.platform_distribution).map(([platform, count]) => {
                const total = Object.values(analytics.platform_distribution).reduce((a, b) => a + b, 0);
                const percentage = Math.round((count / total) * 100);
                
                return (
                  <div key={platform} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{platform}</span>
                      <span className="text-muted-foreground">{percentage}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        className="h-full bg-primary rounded-full"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaign Performance */}
      {analytics?.campaign_performance?.length > 0 && (
        <Card className="glass-card border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Campaign Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.campaign_performance.slice(0, 5).map((campaign) => (
                <div 
                  key={campaign.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{campaign.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {campaign.impressions?.toLocaleString()} impressions
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {campaign.engagements?.toLocaleString()} engagements
                    </Badge>
                    <Badge 
                      className={`text-xs ${campaign.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}
                    >
                      {campaign.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!analytics?.campaign_performance?.length && (
        <Card className="glass-card border-white/5">
          <CardContent className="p-8 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-muted-foreground">No analytics data yet</p>
            <p className="text-sm text-muted-foreground">Create campaigns to start tracking performance</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
